<template lang="pug">
  #app
    app-header

    main.main-width
      router-view

    app-footer
</template>

<script>
import AppHeader from '@/components/core/AppHeader.vue';
import AppFooter from '@/components/core/AppFooter.vue';

export default {
  name: 'App',
  components: {
    AppHeader,
    AppFooter,
  },
  created() {
    if (this.$store.state.user) {
      this.global_verifySession(this.$store.state.user.loginKey, this.$store.state.user.ip);
    }
  },
};
</script>

<style lang="scss">
@import '@/style/definitions';

* {
  margin: 0;
  padding: 0;
  border: 0;
  box-sizing: border-box;
}

body {
  background: $color-bg;
  color: $color-fg;
  line-height: 18px;
  font-size: 13px;
  font-family: 'PT Sans Caption', sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-rendering: optimizeLegibility;
  -webkit-text-size-adjust: 100%;
  -moz-text-size-adjust: 100%;
  -ms-text-size-adjust: 100%;
}

a {
  text-decoration: none;
  color: $color-fg;

  &.highlight {
    color: $color-accent;

    &:hover, &:focus {
      color: lighten($color-accent, 10%);
    }
  }

  &:hover, &:focus {
    color: $color-accent;
  }
}

input {
  font-family: 'PT Sans Caption', sans-serif;
}

.main-width {
  max-width: 1340px;
  padding: 0 15px;
  margin: 0 auto;
  position: relative;

  @media screen and (max-width: 979px) {
    padding: 0;
  }
}

.spacer {
  height: 32px;
}

.block-wrapper {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
}

.flex-right {
  margin-left: auto !important;
}

.mobile {
  display: none;

  @media screen and (max-width: 979px) {
    display: block;
  }
}

@media screen and (max-width: 979px) {
  .mobile-hide {
    display: none;
  }
}

main {
  min-height: calc(100vh - 322px); // 174 (header) + 64 (margins) + 84 (footer)

  @media screen and (max-width: 979px) {
    min-height: calc(100vh - 394px);
  }

  h2 {
    font-size: 26px;
    font-weight: 400;
    line-height: 1.2;

    @media screen and (max-width: 979px) {
      padding-left: 8px;
    }
  }

  h3 {
    position: relative;
    font-size: 15px;
    padding: 16px;
    background: $color-header-bg;
    border-top: 4px solid $color-accent;
    border-bottom: 1px solid $color-border;
    text-transform: uppercase;
    font-weight: 700;
  }

  .page-header {
    display: flex;
    flex-direction: row;
    padding: 12px 0 24px;

    .button {
      position: relative;
      color: $color-fg;
      text-decoration: none;
      font-size: 14px;
      background: $color-accent;
      padding: 8px;
      border-radius: 4px;
      margin-left: 8px;

      &:hover, &:focus {
        background-image:
          linear-gradient(to bottom, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.08) 100%);
      }

      &.icon {
        padding-left: 32px;
      }

      svg {
        position: absolute;
        top: 50%;
        left: 8px;
        transform: translateY(-50%);
        fill: $color-fg;
        width: 16px;
        height: 16px;
      }
    }

    select {
      background: $color-content-bg;
      border: 1px solid $color-border;
      border-radius: 3px;
      width: 240px;
      padding: 7px;
      font-size: 13px;
      color: $color-desc;
    }

    .content-search {
      margin-left: auto;
      max-width: 480px;
      width: 100%;
      display: flex;
      flex-direction: row;

      select {
        border-top-right-radius: 0;
        border-bottom-right-radius: 0;
      }

      input {
        background: $color-header-bg;
        border: 1px solid $color-border;
        border-radius: 3px;
        width: 100%;
        padding: 7px;
        font-size: 15px;
        color: $color-fg;

        &.filtered {
          border-left: 0;
          border-top-left-radius: 0;
          border-bottom-left-radius: 0;
          width: calc(100% - 120px);
        }

        &.buttoned {
          border-right: 0;
          border-top-right-radius: 0;
          border-bottom-right-radius: 0;
          width: calc(100% - 39px);
        }

        &.filtered.buttoned {
          width: calc(100% - 120px - 39px);
        }
      }

      .search-go {
        height: 39px;

        svg {
          cursor: pointer;
          background: $color-accent;
          padding: 6px;
          width: 39px;
          height: 39px;
          fill: $color-fg;
          border-top-right-radius: 3px;
          border-bottom-right-radius: 3px;

          &:hover, &:focus {
            background-image:
              linear-gradient(to bottom, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.08) 100%);
          }
        }
      }

      @media screen and (max-width: 979px) {
        margin-top: 8px;
      }
    }

    @media screen and (max-width: 979px) {
      display: block;
    }
  }

  .server {
    .server-info {
      width: 50%;
      padding-right: 16px;

      ul {
        list-style: none;
        display: flex;
        flex-direction: column;
        background: $color-content-bg;
        padding: 16px;

        li {
          display: inline-block;
          padding: 6px 0;
          white-space: nowrap;
          text-overflow: ellipsis;
          overflow: hidden;

          span.ip {
            cursor: pointer;

            &:hover, &:focus {
              color: $color-accent;
            }
          }
        }
      }

      @media screen and (max-width: 979px) {
        width: 100%;
        padding-right: 0;
        padding-bottom: 32px;
      }
    }

    .my-stats {
      width: 50%;
      padding-left: 16px;

      ul {
        list-style: none;
        display: flex;
        flex-direction: column;
        background: $color-content-bg;
        padding: 16px;

        li {
          display: inline-block;
          padding: 6px 0;
        }
      }

      @media screen and (max-width: 979px) {
        width: 100%;
        padding-left: 0;
      }
    }

    h3.player-list-title {
      position: relative;
      cursor: pointer;

      svg {
        position: absolute;
        top: 50%;
        right: 16px;
        fill: $color-fg;
        opacity: 0;
        transition: .2s ease-in-out;
        transform: translateY(-50%) rotate(90deg);
      }

      &.active svg {
        transform: translateY(-50%) rotate(0deg);
      }

      &:hover svg, &:focus svg {
        opacity: 1;
        transform: translateY(-50%) rotate(0deg);
      }
    }

    ul.player-list {
      list-style: none;
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
      background: $color-content-bg;
      padding: 16px;

      li {
        flex-shrink: 0;
        width: 25%;
        display: inline-block;
        padding: 6px 0;

        @media screen and (max-width: 979px) {
          width: calc(100% / 3);
        }

        @media screen and (max-width: 767px) {
          width: 50%;
        }

        @media screen and (max-width: 480px) {
          width: 100%;
        }
      }
    }

    p.last-updated {
      padding: 16px;
      text-align: center;
    }

    @media screen and (max-width: 979px) {
      .page-header {
        margin-bottom: 24px;

        .button {
          top: 16px;

          &.flex-right {
            margin-left: 8px !important;
            top: 16px;
          }
        }
      }
    }
  }

  table {
    width: 100%;
    background: $color-content-bg;

    tr {
      width: 100%;

      &:nth-child(even) {
        background: $color-content-bg-alt;
      }

      td {
        padding: 16px;
      }

      th {
        padding: 16px;
        text-align: left;
        background: $gray;
        font-weight: 700;
        font-size: 14px;
        position: relative;

        .filter {
          cursor: pointer;
          position: absolute;

          &.desc {
            margin-left: 20px;
          }

          svg {
            fill: $color-fg;
            width: 20px;
            height: 20px;
          }
        }
      }
    }
  }
}
</style>
